import { useCallback, useEffect, useState } from 'react';
import { getAccessToken, isLoggedIn } from '../lib/auth';
import { ORG_DOMAINS } from '../lib/config';
import { domainOf } from '../lib/office';
import {
  createTrackedEmail,
  fetchTemplates,
  findCompanyByDomain,
  findPersonByEmail,
  updateTrackedEmail,
  type EmailTemplate,
} from '../lib/twentyClient';
import {
  getBodyHtml,
  getTrackingState,
  insertBodyHtml,
  readComposeDraft,
  saveTrackingState,
  setBodyHtml,
  setSubjectIfEmpty,
  type ComposeDraft,
} from '../lib/compose';
import { applyMerge, buildMergeContext, type MergeContext } from '../lib/merge';
import { rewriteLinks } from '../lib/tracking';
import './styles.css';

type Status =
  | 'loading'
  | 'needsAuth'
  | 'blocked' // running in a non-CLS mailbox
  | 'ready'
  | 'working'
  | 'error';

type Resolved = {
  personId: string | null;
  companyId: string | null;
  merge: MergeContext;
};

// Escape text and turn newlines into <br> so a plain-text template body keeps
// its line breaks when inserted as HTML. A template that already contains markup
// is inserted as-is.
const templateBodyToHtml = (body: string): string => {
  if (/<[a-z][\s\S]*>/i.test(body)) return body;
  const escaped = body
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped.replace(/\r?\n/g, '<br>');
};

export const ComposePane = () => {
  const [status, setStatus] = useState<Status>('loading');
  const [draft, setDraft] = useState<ComposeDraft | null>(null);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [resolved, setResolved] = useState<Resolved | null>(null);
  const [existingRecordId, setExistingRecordId] = useState<string | null>(null);
  const [existingToken, setExistingToken] = useState<string | null>(null);
  const [info, setInfo] = useState('');
  const [missing, setMissing] = useState<string[]>([]);
  const [error, setError] = useState('');

  // Resolve the recipient (first To address) to a CRM Person/Company so we can
  // fill merge fields and link the tracking record. Best-effort - logging a
  // tracked send doesn't require a match.
  const resolveRecipient = useCallback(
    async (token: string, current: ComposeDraft): Promise<Resolved> => {
      const recipient = current.to[0];
      const recipientEmail = recipient?.email ?? '';
      const domain = recipientEmail ? domainOf(recipientEmail) : null;
      const internal = !!domain && ORG_DOMAINS.includes(domain);

      const [person, company] = await Promise.all([
        recipientEmail && !internal
          ? findPersonByEmail(token, recipientEmail)
          : Promise.resolve(null),
        domain && !internal
          ? findCompanyByDomain(token, domain)
          : Promise.resolve(null),
      ]);

      const merge = buildMergeContext({
        firstName: person?.firstName,
        lastName: person?.lastName,
        company: company?.name ?? person?.companyName ?? undefined,
        jobTitle: person?.jobTitle,
        senderName: current.fromName,
        senderEmail: current.fromEmail,
      });
      return { personId: person?.id ?? null, companyId: company?.id ?? null, merge };
    },
    [],
  );

  const loadData = useCallback(
    async (current: ComposeDraft) => {
      const token = await getAccessToken();
      const [tmpls, res, tracking] = await Promise.all([
        fetchTemplates(token),
        resolveRecipient(token, current),
        getTrackingState(),
      ]);
      setTemplates(tmpls);
      setResolved(res);
      setExistingRecordId(tracking.loggedEmailId);
      setExistingToken(tracking.token);
      setStatus('ready');
    },
    [resolveRecipient],
  );

  const load = useCallback(async () => {
    try {
      setStatus('loading');
      setInfo('');
      setError('');
      const current = await readComposeDraft();
      setDraft(current);
      // Multi-account safety: only operate from the CLS Wind mailbox.
      if (!ORG_DOMAINS.includes(domainOf(current.fromEmail) ?? '')) {
        setStatus('blocked');
        return;
      }
      if (isLoggedIn()) await loadData(current);
      else setStatus('needsAuth');
    } catch (caught) {
      setError((caught as Error).message);
      setStatus('error');
    }
  }, [loadData]);

  // Re-read the draft and resolve the recipient against the CRM *now*. The user
  // can add/change the To recipient after the pane is already open, so merge
  // fields and the person/company link must be resolved at action time, not just
  // at load - resolving once at load would miss a later-added recipient.
  const refreshResolved = useCallback(
    async (token: string): Promise<{ draft: ComposeDraft; resolved: Resolved }> => {
      const current = await readComposeDraft();
      const res = await resolveRecipient(token, current);
      setDraft(current);
      setResolved(res);
      return { draft: current, resolved: res };
    },
    [resolveRecipient],
  );

  useEffect(() => {
    load();
    // Keep the resolved recipient in sync as the user edits the To/Cc fields, so
    // the displayed match and merge context reflect the current recipient.
    // RecipientsChanged is compose-only (Mailbox 1.7+); degrade gracefully where
    // unsupported - action-time refreshResolved still guarantees correctness.
    const item = Office.context?.mailbox?.item as
      | { addHandlerAsync?: Function; removeHandlerAsync?: Function }
      | undefined;
    const handler = () => {
      if (!isLoggedIn()) return;
      getAccessToken()
        .then((token) => refreshResolved(token))
        .catch(() => {});
    };
    try {
      item?.addHandlerAsync?.(Office.EventType.RecipientsChanged, handler);
    } catch {
      // event unsupported on this host - ignore
    }
    return () => {
      try {
        item?.removeHandlerAsync?.(Office.EventType.RecipientsChanged, handler);
      } catch {
        // ignore
      }
    };
  }, [load, refreshResolved]);

  const handleConnect = async () => {
    if (!draft) return;
    try {
      setStatus('loading');
      await getAccessToken(true);
      await loadData(draft);
    } catch (caught) {
      setError((caught as Error).message);
      setStatus('error');
    }
  };

  const handleInsertTemplate = async () => {
    const template = templates.find((t) => t.id === selectedId);
    if (!template) return;
    try {
      setStatus('working');
      setInfo('');
      const token = await getAccessToken();
      // Resolve against the recipient as it is right now (may have been added
      // after the pane opened) so merge fields fill from the real contact.
      const { resolved: res } = await refreshResolved(token);
      const subject = applyMerge(template.subject, res.merge);
      const body = applyMerge(template.body, res.merge);
      await setSubjectIfEmpty(subject.text);
      await insertBodyHtml(templateBodyToHtml(body.text));
      setMissing(Array.from(new Set([...subject.missing, ...body.missing])));
      setInfo(`Inserted "${template.name}".`);
      setStatus('ready');
    } catch (caught) {
      setError((caught as Error).message);
      setStatus('error');
    }
  };

  const handleAddTracking = async () => {
    try {
      setStatus('working');
      setInfo('');
      const token = await getAccessToken();

      // Resolve recipient + re-read the draft now, so the record links to the
      // current recipient's Person/Company and reflects the latest subject/body.
      const { draft: current, resolved: res } = await refreshResolved(token);

      // Reuse the token already stamped on this draft so a second run never
      // double-wraps links or creates a duplicate record.
      const trackToken = existingToken ?? crypto.randomUUID();
      const html = await getBodyHtml();
      const { html: rewritten, count } = rewriteLinks(html, trackToken);
      if (count > 0) await setBodyHtml(rewritten);

      const recordInput = {
        subject: current.subject,
        toEmails: current.to.map((r) => r.email),
        body: rewritten,
        personId: res.personId,
        companyId: res.companyId,
      };

      if (existingRecordId) {
        await updateTrackedEmail(token, existingRecordId, recordInput);
      } else {
        const { id } = await createTrackedEmail(token, {
          ...recordInput,
          fromEmail: current.fromEmail,
          sentAt: new Date().toISOString(),
          token: trackToken,
        });
        await saveTrackingState(trackToken, id);
        setExistingRecordId(id);
        setExistingToken(trackToken);
      }

      setInfo(
        count > 0
          ? `Tracking added — ${count} link${count === 1 ? '' : 's'} tracked. Send as normal; clicks will appear on the contact in Breeze.`
          : 'Logged for engagement, but no links were found to track. Add a link, then run this again.',
      );
      setStatus('ready');
    } catch (caught) {
      setError((caught as Error).message);
      setStatus('error');
    }
  };

  if (status === 'loading') {
    return <div className="pane center">Loading…</div>;
  }
  if (status === 'error') {
    return (
      <div className="pane">
        <div className="banner error">Something went wrong</div>
        <p className="muted">{error}</p>
        <button onClick={load}>Try again</button>
      </div>
    );
  }

  const recipientEmail = draft?.to[0]?.email ?? '';
  const working = status === 'working';

  return (
    <div className="pane">
      <h1>Breeze</h1>

      {draft && (
        <div className="card">
          <div className="subject">{draft.subject || '(no subject yet)'}</div>
          <div className="muted">
            {recipientEmail ? `To: ${recipientEmail}` : 'No recipient yet'}
          </div>
        </div>
      )}

      {status === 'blocked' && (
        <div className="banner error">
          Templates and tracking are only available from your CLS Wind mailbox.
          This draft is from <strong>{draft?.fromEmail}</strong>.
        </div>
      )}

      {status === 'needsAuth' && (
        <>
          <p className="muted">Connect your Breeze account to continue.</p>
          <button className="primary" onClick={handleConnect}>
            Connect to Breeze
          </button>
        </>
      )}

      {(status === 'ready' || working) && resolved && (
        <>
          <div className="field">
            <span>Template</span>
            <select
              className="stage-select"
              value={selectedId}
              onChange={(event) => setSelectedId(event.target.value)}
            >
              <option value="">Select a template…</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <button
            className="primary"
            disabled={working || !selectedId}
            onClick={handleInsertTemplate}
          >
            Insert template
          </button>
          {missing.length > 0 && (
            <p className="muted">
              Fill in manually: {missing.map((f) => `{{${f}}}`).join(', ')}
            </p>
          )}

          <hr style={{ width: '100%', border: 'none', borderTop: '1px solid var(--border)' }} />

          <p className="muted">
            Rewrites links so clicks are recorded on the contact in Breeze. You
            still send the email yourself in Outlook.
          </p>
          <button
            className="secondary"
            disabled={working}
            onClick={handleAddTracking}
          >
            {existingRecordId ? 'Update tracking' : 'Add tracking & log'}
          </button>

          {info && <div className="banner success">{info}</div>}
        </>
      )}

      <div className="version">Breeze add-in v{__APP_VERSION__}</div>
    </div>
  );
};
